/**
 * MultiSelect.jsx · v3.2.0 · Feb-2026
 * ────────────────────────────────────────────────────────────────
 * Selector multi-selección con buscador integrado, chips y contador.
 * Uso:
 *   <MultiSelect
 *     label="Sucursales"
 *     options={[{value, label, sublabel?}, ...]}
 *     selected={[value1, value2]}
 *     onChange={(arr) => ...}
 *     placeholder="Todas las sucursales"
 *   />
 */
import { useState, useMemo, useRef, useEffect } from "react";
import { Search, X, Check, ChevronDown } from "lucide-react";
import FloatingPanel, { isInsideFloating } from "./ui/FloatingPanel";   // v101.98.0

export default function MultiSelect({
  label,
  options = [],
  selected = [],
  onChange,
  placeholder = "Todos",
  icon,
  testId,
  maxChips = 3,
  width = "auto",
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  const btnRef = useRef(null);
  const set = new Set(selected);
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target) && !isInsideFloating(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return options;
    return options.filter((o) =>
      (o.label || "").toLowerCase().includes(t) ||
      (o.sublabel || "").toLowerCase().includes(t) ||
      String(o.value).toLowerCase().includes(t)
    );
  }, [q, options]);
  const toggle = (v) => {
    const s = new Set(selected);
    if (s.has(v)) s.delete(v); else s.add(v);
    onChange([...s]);
  };
  const clear = () => onChange([]);
  const summary = selected.length === 0
    ? placeholder
    : selected.length === 1
      ? (options.find((o) => o.value === selected[0])?.label || "1 elemento")
      : selected.length <= maxChips
        ? selected.map((v) => options.find((o) => o.value === v)?.label || v).join(", ")
        : `${selected.length} seleccionados`;
  return (
    <div className="relative inline-block" style={{ width }} ref={ref} data-testid={testId || `multiselect-${label}`}>
      {label && <label className="block text-[10px] font-bold text-gray-600 mb-0.5 uppercase tracking-wide">{label}</label>}
      <button
        ref={btnRef}
        onClick={() => setOpen((v) => !v)}
        className={`w-full min-w-[180px] px-2 py-1.5 border rounded text-xs bg-white flex items-center gap-1.5 hover:bg-gray-50 transition-colors ${selected.length > 0 ? "border-blue-500 bg-blue-50" : "border-gray-300"}`}
        data-testid={`${testId || label}-toggle`}
      >
        {icon && <span className="text-gray-400">{icon}</span>}
        <span className={`flex-1 truncate text-left ${selected.length === 0 ? "text-gray-500" : "text-gray-800 font-medium"}`}>{summary}</span>
        {selected.length > 0 && (
          <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-600 text-white font-bold">{selected.length}</span>
        )}
        <ChevronDown size={11} className={`text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}/>
      </button>
      {open && (
        <FloatingPanel anchorRef={btnRef} className="w-72 bg-white border rounded-lg shadow-xl" data-testid={`${testId || label}-panel`}>
          <div className="p-2 border-b bg-slate-50 flex items-center gap-1.5 sticky top-0">
            <Search size={11} className="text-gray-400"/>
            <input
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder={`Buscar en ${label?.toLowerCase() || "opciones"}…`}
              className="flex-1 text-xs bg-transparent outline-none"
              autoFocus
              data-testid={`${testId || label}-search`}
            />
            {selected.length > 0 && (
              <button onClick={clear} className="text-[10px] text-red-600 hover:underline flex items-center gap-0.5" data-testid={`${testId || label}-clear`}>
                <X size={10}/>Limpiar
              </button>
            )}
          </div>
          <div className="max-h-72 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="p-4 text-center text-[11px] text-gray-400">Sin resultados</div>
            ) : filtered.map((o) => {
              const on = set.has(o.value);
              return (
                <button
                  key={o.value}
                  onClick={() => toggle(o.value)}
                  className={`w-full text-left px-2 py-1.5 text-xs flex items-center gap-2 border-b border-gray-100 last:border-0 hover:bg-blue-50 ${on ? "bg-blue-50" : ""}`}
                  data-testid={`${testId || label}-item-${o.value}`}
                >
                  <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center flex-shrink-0 ${on ? "bg-blue-600 border-blue-600" : "border-gray-300"}`}>
                    {on && <Check size={9} className="text-white"/>}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="truncate font-medium">{o.label}</div>
                    {o.sublabel && <div className="text-[9px] text-gray-500 truncate">{o.sublabel}</div>}
                  </div>
                </button>
              );
            })}
          </div>
          <div className="p-1.5 border-t bg-slate-50 flex items-center justify-between text-[10px] text-gray-500">
            <span>{selected.length}/{options.length} seleccionados</span>
            <button onClick={() => setOpen(false)} className="text-blue-600 hover:underline font-bold" data-testid={`${testId || label}-done`}>
              Listo
            </button>
          </div>
        </FloatingPanel>
      )}
    </div>
  );
}
